import type { ComponentType } from "react";
import { Body } from "@/sprout/typography";
import { SecondaryButtonLink } from "@sprout/buttons";
import type { IconProps } from "@/sprout/icons/base";
import type { GhrTask } from "@/types/task";
import useTranslation from "@hooks/use_translation";
import useTaskStatusText from "@hooks/use_task_status_text";
import GHR_TASK_CONFIG from "@/config/ghr_task_config";

type BannerProps = {
  icon: ComponentType<IconProps>;
  title: string;
  actionText: string;
  actionUrl: string | null;
  statusText: string;
};

const Banner = ({ icon: Icon, title, actionText, actionUrl, statusText }: BannerProps) => {
  if (!actionUrl) {
    return null;
  }

  return (
    <div className="application-task-banner w-full bg-white border border-gray-700 rounded-lg px-6 py-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <div className="flex items-center justify-center bg-gray-500 rounded w-[30px] h-[30px]">
              <Icon size="sm" />
            </div>
            <Body className="!text-xs !ml-1" metadata>
              {statusText}
            </Body>
          </div>
          <Body medium>{title}</Body>
        </div>
        <div className="md:shrink-0">
          <SecondaryButtonLink href={actionUrl} size="medium" external>
            {actionText}
          </SecondaryButtonLink>
        </div>
      </div>
    </div>
  );
};

const ApplicationTaskBanner = ({ task }: { task: GhrTask }) => {
  const { t } = useTranslation("tasks");
  const statusText = useTaskStatusText(task);
  const config = GHR_TASK_CONFIG[task.taskable_type];

  if (!config) {
    return null;
  }

  return (
    <Banner
      icon={config.icon}
      title={t(config.titleKey)}
      actionText={t(config.actionTextKey)}
      actionUrl={task.url}
      statusText={statusText}
    />
  );
};

export default ApplicationTaskBanner;
